/**
 * /dashboard — Story 7.12 Dashboard v1 + Story 7.34 (range de data custom).
 *
 * Six KPI cards + conversion trend line chart. Polls
 * `/admin/metrics/dashboard` every 30s via TanStack Query
 * (`refetchInterval`) — auto-refresh só roda no modo preset; num range
 * customizado (histórico, geralmente no passado) não faz sentido reconsultar
 * a cada 30s.
 *
 * Período pode ser um preset (7/14/30d, relativo a "agora") ou um range
 * customizado (`from`/`to` explícitos, qualquer data). Todas as métricas
 * que variam com o período (conversão, opt-out, latência IA, trend, valor
 * investido, métricas por template) respeitam o range escolhido; conversas
 * ativas / callbacks pendentes / follow-ups 24h são snapshots operacionais
 * sempre atuais, independentes do filtro.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarCheck,
  ChevronDown,
  Clock,
  DollarSign,
  ExternalLink,
  Gauge,
  Lightbulb,
  MessageSquareText,
  MessagesSquare,
  PhoneCall,
  Rocket,
  Target,
  TrendingDown,
  UserMinus,
  Users,
  Wallet,
} from 'lucide-react';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ApiError, apiClient } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';

type Period = 7 | 14 | 30;
type PeriodMode = 'preset' | 'custom';

/** `YYYY-MM-DD` → `DD/MM`, pra rótulo curto nos cards. */
function formatShortDate(isoDate: string): string {
  const [, m, d] = isoDate.split('-');
  return `${d}/${m}`;
}

interface FollowupsBreakdown {
  sent: number;
  failed: number;
  skipped_window: number;
  skipped_opted_out: number;
  skipped_24h_gate: number;
}

interface ActiveCampaign {
  id: string;
  name: string;
}

interface CampaignListResponse {
  data: Array<{ id: string; name: string; active: boolean }>;
}

interface TemplateMetric {
  /** Story 7.35 (extensão) — nome da campanha; `'desconhecida'` se apagada. */
  campaign: string;
  template: string;
  firstContacts: number;
  responses: number;
  responseRate: number;
  /** Story 7.35 — custo Meta estimado desse template no período; `null` se sem preço cadastrado. */
  costUsd: number | null;
}

/** 2026-07-31 — comparativo por campanha (redesign visual). */
interface CampaignBreakdown {
  campaign: string;
  firstContacts: number;
  responses: number;
  responseRate: number;
  converted: number;
  convertedElsewhere: number;
  revenueUsd: number;
  costUsd: number;
}

interface IntentDistribution {
  alta: number;
  media: number;
  baixa: number;
}

interface CallbackStatusDistribution {
  pending: number;
  executing: number;
  executed: number;
  failed: number;
  cancelled: number;
  expired: number;
}

interface LossReason {
  reason: string;
  count: number;
}

interface RecentInteraction {
  conversationId: string;
  customerName: string;
  campaign: string;
  status: string;
  closedReason: string | null;
  lastActivityAt: string;
}

interface DashboardMetrics {
  periodFrom: string;
  periodTo: string;
  generatedAt: string;
  activeConversations: { today: number; last7d: number; last30d: number };
  conversionRate: number;
  optOutRate: number;
  pendingCallbacks: number;
  followups24h: FollowupsBreakdown;
  aiLatency: { p50: number | null; p95: number | null; samples: number };
  conversionTrend: Array<{ date: string; converted: number; declined: number }>;
  investedValueUsd: number;
  templateMetrics: TemplateMetric[];
  /** Story 7.35 — métricas de topo agregadas + Taxa de Ascensão fixa. */
  messagesSent: number;
  messagesResponded: number;
  responseRateAggregate: number;
  ascensionRate: number;
  /** 2026-07-30 — volume de conversas fechadas via converted_elsewhere (compra em outro canal, não é conversão da IA). */
  convertedElsewhereCount: number;
  /** 2026-07-31 — redesign visual (case de dashboard de IA SDR). */
  campaignBreakdown: CampaignBreakdown[];
  intentDistribution: IntentDistribution;
  callbackStatusDistribution: CallbackStatusDistribution;
  lossReasons: LossReason[];
  recentInteractions: RecentInteraction[];
}

interface DashboardInsightPriority {
  rank: number;
  title: string;
  description: string;
  severity: 'critico' | 'atencao';
}

interface DashboardInsights {
  generatedAt: string;
  periodFrom: string;
  periodTo: string;
  healthScore: number;
  healthLabel: string;
  insight: { title: string; body: string };
  priorities: DashboardInsightPriority[];
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatPercent(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

/** USD com 2 casas — "valor investido" agrega IA + Meta + Infra (Story 7.32). */
function formatUsd(v: number): string {
  return `US$ ${v.toFixed(2)}`;
}

function formatMs(v: number | null): string {
  if (v === null) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  return `${v}ms`;
}

function sumSkipped(b: FollowupsBreakdown): number {
  return b.skipped_window + b.skipped_opted_out + b.skipped_24h_gate;
}

/** 2026-07-31 — cor do Índice de saúde comercial (score 0-100, calculado em código). */
function healthColorClasses(score: number): string {
  if (score >= 70) return 'text-green-700 dark:text-green-300';
  if (score >= 40) return 'text-amber-700 dark:text-amber-300';
  return 'text-red-700 dark:text-red-300';
}

function healthBarColor(score: number): string {
  if (score >= 70) return 'bg-green-600';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-red-600';
}

const SEVERITY_BADGE_CLASSES: Record<DashboardInsightPriority['severity'], string> = {
  critico: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  atencao: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
};

const SEVERITY_LABELS: Record<DashboardInsightPriority['severity'], string> = {
  critico: 'Crítico',
  atencao: 'Atenção',
};

/** Rótulos amigáveis pra `closed_reason`/`status` bruto — mantém o valor original como fallback. */
const LOSS_REASON_LABELS: Record<string, string> = {
  user_refused: 'Cliente recusou',
  superseded_by_new_campaign: 'Substituída por nova campanha',
  // 2026-07-31 (fix QA LR-1) — motivo real gravado por InactivityNudgeService
  // (lead esfriou após follow-up, status='inactive'); faltava o rótulo.
  no_response_after_followup: 'Sem resposta após follow-up',
  declined: 'Declinou',
  closed: 'Fechada (sem motivo detalhado)',
  inactive: 'Inativa (sem motivo detalhado)',
};

function lossReasonLabel(reason: string): string {
  return LOSS_REASON_LABELS[reason] ?? reason;
}

const CALLBACK_STATUS_LABELS: Record<keyof CallbackStatusDistribution, string> = {
  pending: 'Pendente',
  executing: 'Em execução',
  executed: 'Executado',
  failed: 'Falhou',
  cancelled: 'Cancelado',
  expired: 'Expirado',
};

const CALLBACK_STATUS_COLORS: Record<keyof CallbackStatusDistribution, string> = {
  pending: 'hsl(217 91% 60%)',
  executing: 'hsl(38 92% 50%)',
  executed: 'hsl(142 72% 29%)',
  failed: 'hsl(0 84% 55%)',
  cancelled: 'hsl(220 9% 46%)',
  expired: 'hsl(280 65% 60%)',
};

const CONVERSATION_STATUS_LABELS: Record<string, string> = {
  active: 'Ativa',
  converted: 'Convertida',
  declined: 'Declinou',
  closed: 'Fechada',
  error: 'Erro',
  scheduled_callback: 'Callback agendado',
  human_handoff: 'Com humano',
  scheduled: 'Agendada',
  awaiting_response: 'Aguardando resposta',
  inactive: 'Inativa',
};

function conversationStatusLabel(status: string): string {
  return CONVERSATION_STATUS_LABELS[status] ?? status;
}

export function DashboardPage() {
  const [periodMode, setPeriodMode] = useState<PeriodMode>('preset');
  const [period, setPeriod] = useState<Period>(30);
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const hasValidCustomRange =
    periodMode === 'custom' && customFrom !== '' && customTo !== '' && customFrom <= customTo;
  const hasInvalidCustomRange =
    periodMode === 'custom' && customFrom !== '' && customTo !== '' && customFrom > customTo;
  const rangeLabel =
    periodMode === 'custom' && hasValidCustomRange
      ? `${formatShortDate(customFrom)}–${formatShortDate(customTo)}`
      : `${period}d`;
  // Quando o set está vazio o usuário ainda não viu a primeira carga; quando
  // contém todos os ids ativos não mandamos o param (backend agrega todas).
  // Quando é um subset estrito, vai como CSV pra rota.
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);

  const campaignsQuery = useQuery({
    queryKey: ['dashboard', 'campaigns', 'active'],
    queryFn: () => apiClient.get<CampaignListResponse>('/admin/campaigns?active=true&pageSize=100'),
    staleTime: 60_000,
  });

  const activeCampaigns = useMemo<ActiveCampaign[]>(
    () =>
      (campaignsQuery.data?.data ?? [])
        .filter((c) => c.active)
        .map((c) => ({ id: c.id, name: c.name })),
    [campaignsQuery.data],
  );

  // Primeira carga: marca todas. Mudanças subsequentes na lista (campanha
  // ativada/desativada) preservam o que o usuário marcou — sem resetar.
  // Dependência restrita a `activeCampaigns.length` de propósito: incluir
  // `selectedCampaignIds`/`activeCampaigns` inteiros re-rodaria o efeito a
  // cada toggle de seleção do usuário, resetando a escolha dele.
  useEffect(() => {
    if (selectedCampaignIds.length === 0 && activeCampaigns.length > 0) {
      setSelectedCampaignIds(activeCampaigns.map((c) => c.id));
    }
  }, [activeCampaigns.length]);

  const allSelected =
    activeCampaigns.length > 0 && selectedCampaignIds.length === activeCampaigns.length;

  // CSV mandado pro backend só quando há subset estrito. "Todas" omite o
  // param — backend agrega tudo. "Nenhuma" manda um id sentinela impossível
  // pra zerar resultados (alternativa: não fazer fetch, mas a UX fica mais
  // confusa que zerar).
  const campaignIdsCsv = useMemo(() => {
    if (allSelected) return undefined;
    if (selectedCampaignIds.length === 0) return '00000000-0000-0000-0000-000000000000';
    return selectedCampaignIds.join(',');
  }, [allSelected, selectedCampaignIds]);

  const query = useQuery({
    queryKey: [
      'dashboard',
      periodMode === 'custom' ? ['custom', customFrom, customTo] : ['preset', period],
      campaignIdsCsv,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (periodMode === 'custom' && hasValidCustomRange) {
        params.set('from', customFrom);
        params.set('to', customTo);
      } else {
        params.set('period', String(period));
      }
      if (campaignIdsCsv) params.set('campaignIds', campaignIdsCsv);
      return apiClient.get<DashboardMetrics>(`/admin/metrics/dashboard?${params.toString()}`);
    },
    // Num range custom incompleto/inválido não busca ainda; auto-refresh só
    // faz sentido no modo preset (range custom costuma ser histórico fixo).
    enabled: periodMode === 'preset' || hasValidCustomRange,
    refetchInterval: periodMode === 'preset' ? 30_000 : false,
  });

  // 2026-07-31 — Índice de saúde / Insight / Prioridades: escopo fixo (7d,
  // todas as campanhas), cache real de 3h no backend. O front só evita
  // reconsultar toda hora — não faz sentido pollar 30s uma coisa que o
  // backend só recalcula a cada 3h.
  const insightsQuery = useQuery({
    queryKey: ['dashboard', 'insights'],
    queryFn: () => apiClient.get<DashboardInsights>('/admin/metrics/dashboard/insights'),
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
  });

  const toggleCampaign = (id: string) => {
    setSelectedCampaignIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllCampaigns = () => setSelectedCampaignIds(activeCampaigns.map((c) => c.id));
  const clearCampaigns = () => setSelectedCampaignIds([]);

  const campaignLabel = (() => {
    if (campaignsQuery.isLoading) return 'Carregando…';
    if (activeCampaigns.length === 0) return 'Nenhuma ativa';
    if (allSelected) return `Todas as ${activeCampaigns.length} ativas`;
    if (selectedCampaignIds.length === 0) return 'Nenhuma';
    if (selectedCampaignIds.length === 1) {
      const c = activeCampaigns.find((x) => x.id === selectedCampaignIds[0]);
      return c?.name ?? '1 selecionada';
    }
    return `${selectedCampaignIds.length} de ${activeCampaigns.length}`;
  })();

  // Funil da Rafaela (adaptado do dashboard de referência do IA SDR — sem
  // inventar estágios que não existem no domínio dela, como "qualificados"
  // ou "agendados"): primeiros contatos → responderam → convertidos.
  // "Declinados" não é um estágio sequencial do MESMO funil (uma conversa
  // pode estar apenas ativa, nem convertida nem declinada ainda) — por isso
  // fica no resumo rápido, não como 4º estágio do funil.
  const totalConverted = useMemo(
    () => query.data?.conversionTrend.reduce((sum, b) => sum + b.converted, 0) ?? 0,
    [query.data],
  );
  const totalDeclined = useMemo(
    () => query.data?.conversionTrend.reduce((sum, b) => sum + b.declined, 0) ?? 0,
    [query.data],
  );
  const funnelStages = query.data
    ? [
        { label: 'Primeiros contatos', value: query.data.messagesSent },
        { label: 'Responderam', value: query.data.messagesResponded },
        { label: 'Convertidos', value: totalConverted },
      ]
    : [];
  // QA finding LOW-1 (Story 7.35): "Em andamento" é uma APROXIMAÇÃO, não um
  // número exato — `messagesResponded` vem de `templateMetrics` (filtrado
  // por quando o 1º template foi ENVIADO) enquanto `totalConverted`/
  // `totalDeclined` vêm de `conversionTrend` (filtrado por quando a
  // CONVERSA foi criada). Na prática as duas datas costumam coincidir (mesmo
  // fluxo de disparo), mas não há garantia formal — por isso o rótulo deixa
  // isso explícito e o `Math.max(0, ...)` evita render de número negativo
  // caso as populações divirjam mais do que o esperado.
  const donutData = query.data
    ? [
        { name: 'Convertidos', value: totalConverted, color: 'hsl(142 72% 29%)' },
        { name: 'Declinados', value: totalDeclined, color: 'hsl(0 84% 55%)' },
        {
          name: 'Em andamento (aprox.)',
          value: Math.max(0, query.data.messagesResponded - totalConverted - totalDeclined),
          color: 'hsl(217 91% 60%)',
        },
      ].filter((d) => d.value > 0)
    : [];

  const intentDonutData = query.data
    ? [
        {
          name: 'Alta intenção',
          value: query.data.intentDistribution.alta,
          color: 'hsl(142 72% 29%)',
        },
        {
          name: 'Média intenção',
          value: query.data.intentDistribution.media,
          color: 'hsl(38 92% 50%)',
        },
        {
          name: 'Baixa intenção',
          value: query.data.intentDistribution.baixa,
          color: 'hsl(220 9% 46%)',
        },
      ].filter((d) => d.value > 0)
    : [];
  const totalIntentLeads = intentDonutData.reduce((sum, d) => sum + d.value, 0);

  const callbackDonutData = query.data
    ? (Object.keys(CALLBACK_STATUS_LABELS) as Array<keyof CallbackStatusDistribution>)
        .map((key) => ({
          name: CALLBACK_STATUS_LABELS[key],
          value: query.data!.callbackStatusDistribution[key],
          color: CALLBACK_STATUS_COLORS[key],
        }))
        .filter((d) => d.value > 0)
    : [];
  const totalCallbacks = callbackDonutData.reduce((sum, d) => sum + d.value, 0);

  const totalLossReasons = query.data
    ? query.data.lossReasons.reduce((sum, r) => sum + r.count, 0)
    : 0;

  const totalRevenueUsd = query.data
    ? query.data.campaignBreakdown.reduce((sum, c) => sum + c.revenueUsd, 0)
    : 0;
  const roiApprox =
    query.data && query.data.investedValueUsd > 0
      ? totalRevenueUsd / query.data.investedValueUsd
      : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard Rafaela</h1>
          <p className="text-sm text-muted-foreground">
            Visão geral da operação — auto-refresh a cada 30s.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="Período"
            className="inline-flex rounded-md border bg-background p-0.5 text-sm"
          >
            {([7, 14, 30] as Period[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setPeriodMode('preset');
                  setPeriod(p);
                }}
                aria-pressed={periodMode === 'preset' && period === p}
                className={`rounded px-3 py-1 transition-colors ${
                  periodMode === 'preset' && period === p
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent'
                }`}
              >
                {p}d
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPeriodMode('custom')}
              aria-pressed={periodMode === 'custom'}
              className={`rounded px-3 py-1 transition-colors ${
                periodMode === 'custom'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent'
              }`}
            >
              Personalizado
            </button>
          </div>
          {periodMode === 'custom' ? (
            <div className="flex items-center gap-1">
              <Input
                type="date"
                aria-label="Data inicial"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-8 w-36 text-sm"
              />
              <span className="text-muted-foreground">–</span>
              <Input
                type="date"
                aria-label="Data final"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-8 w-36 text-sm"
              />
            </div>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={activeCampaigns.length === 0}
                aria-label="Filtrar por campanha"
              >
                <span>Campanhas: {campaignLabel}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {activeCampaigns.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.id}
                  checked={selectedCampaignIds.includes(c.id)}
                  onCheckedChange={() => toggleCampaign(c.id)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {c.name}
                </DropdownMenuCheckboxItem>
              ))}
              {activeCampaigns.length > 0 ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem onSelect={selectAllCampaigns}>Marcar todas</DropdownMenuItem>
              <DropdownMenuItem onSelect={clearCampaigns}>Limpar seleção</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {hasInvalidCustomRange ? (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Data inicial precisa ser anterior ou igual à data final.
          </CardContent>
        </Card>
      ) : null}

      {query.error ? (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            {humanizeError(query.error, 'Falha ao carregar métricas.')}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <KpiCard title={`Valor investido (${rangeLabel})`} icon={DollarSign} color="green">
          <div data-testid="kpi-invested-value" className="text-2xl font-semibold">
            {query.isLoading ? '—' : formatUsd(query.data?.investedValueUsd ?? 0)}
          </div>
        </KpiCard>

        <KpiCard title="Conversas ativas" icon={Users} color="blue">
          <div className="space-y-0.5 text-sm">
            <div className="text-2xl font-semibold">
              {query.isLoading ? '—' : (query.data?.activeConversations.today ?? 0)}
            </div>
            <div className="text-xs text-muted-foreground">hoje</div>
            <div className="mt-2 flex justify-between gap-1 text-xs text-muted-foreground">
              <span>7d: {query.data?.activeConversations.last7d ?? 0}</span>
              <span>30d: {query.data?.activeConversations.last30d ?? 0}</span>
            </div>
          </div>
        </KpiCard>

        <KpiCard title="Taxa de conversão (Recuperados)" icon={Rocket} color="green">
          <div className="text-2xl font-semibold text-green-700 dark:text-green-300">
            {query.isLoading ? '—' : formatPercent(query.data?.conversionRate ?? 0)}
          </div>
        </KpiCard>

        <KpiCard title="Taxa de Ascensão (CashMiles × Premium)" icon={Rocket} color="purple">
          <div className="text-2xl font-semibold text-purple-700 dark:text-purple-300">
            {query.isLoading ? '—' : formatPercent(query.data?.ascensionRate ?? 0)}
          </div>
        </KpiCard>

        <KpiCard title="Convertidas em outro canal" icon={ExternalLink} color="amber">
          <div className="text-2xl font-semibold text-amber-700 dark:text-amber-300">
            {query.isLoading ? '—' : (query.data?.convertedElsewhereCount ?? 0)}
          </div>
        </KpiCard>

        <KpiCard title="Enviadas" icon={MessageSquareText} color="blue">
          <div className="text-2xl font-semibold">
            {query.isLoading ? '—' : (query.data?.messagesSent ?? 0)}
          </div>
        </KpiCard>

        <KpiCard title="Respondidas" icon={MessagesSquare} color="indigo">
          <div className="text-2xl font-semibold">
            {query.isLoading ? '—' : (query.data?.messagesResponded ?? 0)}
          </div>
        </KpiCard>

        <KpiCard title="Taxa de resposta" icon={MessagesSquare} color="blue">
          <div className="text-2xl font-semibold text-blue-700 dark:text-blue-300">
            {query.isLoading ? '—' : formatPercent(query.data?.responseRateAggregate ?? 0)}
          </div>
        </KpiCard>

        <KpiCard title="Opt-out rate" icon={UserMinus} color="red">
          <div className="text-2xl font-semibold text-red-700 dark:text-red-300">
            {query.isLoading ? '—' : formatPercent(query.data?.optOutRate ?? 0)}
          </div>
        </KpiCard>

        <KpiCard title="Callbacks pendentes" icon={PhoneCall} color="amber">
          <div className="text-2xl font-semibold text-amber-700 dark:text-amber-300">
            {query.isLoading ? '—' : (query.data?.pendingCallbacks ?? 0)}
          </div>
          <Link
            to="/callbacks?status=pending"
            className="mt-1 inline-block text-xs text-primary hover:underline"
          >
            ver lista →
          </Link>
        </KpiCard>

        <KpiCard title="Follow-ups 24h" icon={PhoneCall} color="amber">
          {query.isLoading ? (
            <div className="text-2xl font-semibold">—</div>
          ) : (
            <div className="space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-green-700 dark:text-green-300">sent</span>
                <span className="font-mono">{query.data?.followups24h.sent ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-red-700 dark:text-red-300">failed</span>
                <span className="font-mono">{query.data?.followups24h.failed ?? 0}</span>
              </div>
              <div className="flex items-center justify-between text-muted-foreground">
                <span>skipped</span>
                <span className="font-mono">
                  {query.data ? sumSkipped(query.data.followups24h) : 0}
                </span>
              </div>
            </div>
          )}
          <Link to="/followups" className="mt-1 inline-block text-xs text-primary hover:underline">
            ver histórico →
          </Link>
        </KpiCard>

        <KpiCard title={`Latência IA (p50/p95, ${rangeLabel})`} icon={Rocket} color="indigo">
          {query.isLoading ? (
            <div className="text-2xl font-semibold">—</div>
          ) : query.data && query.data.aiLatency.samples === 0 ? (
            <div className="text-sm italic text-muted-foreground">
              Sem amostras em turn_metrics no período.
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              <div className="text-xl font-semibold">
                {formatMs(query.data?.aiLatency.p50 ?? null)}
              </div>
              <div className="text-xs text-muted-foreground">
                p95: {formatMs(query.data?.aiLatency.p95 ?? null)} · n=
                {query.data?.aiLatency.samples ?? 0}
              </div>
            </div>
          )}
        </KpiCard>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Funil da Rafaela ({rangeLabel})</CardTitle>
            <p className="text-xs text-muted-foreground">
              Primeiros contatos até conversão, no período e campanha(s) selecionados.
            </p>
          </CardHeader>
          <CardContent>
            {query.isLoading ? (
              <div className="h-24 animate-pulse rounded bg-muted" />
            ) : (
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                {funnelStages.map((stage, i) => (
                  <div key={stage.label} className="flex flex-1 items-center gap-2">
                    <div className="flex-1 rounded-lg border bg-muted/30 p-3 text-center">
                      <div className="text-2xl font-semibold">{stage.value}</div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        {stage.label}
                      </div>
                      {i > 0 && funnelStages[0] && funnelStages[0].value > 0 ? (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {formatPercent(stage.value / funnelStages[0].value)} do total
                        </div>
                      ) : null}
                    </div>
                    {i < funnelStages.length - 1 ? (
                      <span className="hidden text-muted-foreground sm:inline">→</span>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Resumo rápido</CardTitle>
            <p className="text-xs text-muted-foreground">
              "Em andamento" no gráfico é uma aproximação (compara respostas por template com
              conversões/declínios por data de criação da conversa).
            </p>
          </CardHeader>
          <CardContent>
            {query.isLoading ? (
              <div className="h-24 animate-pulse rounded bg-muted" />
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-green-700 dark:text-green-300">Convertidos</span>
                  <span className="font-mono font-semibold">{totalConverted}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-red-700 dark:text-red-300">Declinados</span>
                  <span className="font-mono font-semibold">{totalDeclined}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Conversas ativas (hoje)</span>
                  <span className="font-mono font-semibold">
                    {query.data?.activeConversations.today ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Callbacks pendentes</span>
                  <span className="font-mono font-semibold">
                    {query.data?.pendingCallbacks ?? 0}
                  </span>
                </div>
                {donutData.length > 0 ? (
                  <div className="h-40 pt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={donutData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={35}
                          outerRadius={60}
                          paddingAngle={2}
                        >
                          {donutData.map((d) => (
                            <Cell key={d.name} fill={d.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
              <Gauge className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">Índice de saúde comercial</CardTitle>
          </CardHeader>
          <CardContent>
            {insightsQuery.isLoading ? (
              <div className="h-16 animate-pulse rounded bg-muted" />
            ) : (
              <div>
                <div className="flex items-baseline gap-2">
                  <span
                    className={`text-3xl font-semibold ${healthColorClasses(insightsQuery.data?.healthScore ?? 0)}`}
                  >
                    {insightsQuery.data?.healthScore ?? 0}
                  </span>
                  <span className="text-sm text-muted-foreground">/100</span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full ${healthBarColor(insightsQuery.data?.healthScore ?? 0)}`}
                    style={{ width: `${insightsQuery.data?.healthScore ?? 0}%` }}
                  />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {insightsQuery.data?.healthLabel ?? 'Aguardando primeira análise'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
              <Lightbulb className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">Insight principal</CardTitle>
          </CardHeader>
          <CardContent>
            {insightsQuery.isLoading ? (
              <div className="h-16 animate-pulse rounded bg-muted" />
            ) : (
              <div>
                <p className="font-medium">{insightsQuery.data?.insight.title ?? '—'}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {insightsQuery.data?.insight.body ?? 'Sem análise disponível ainda.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">Prioridades de hoje</CardTitle>
          </CardHeader>
          <CardContent>
            {insightsQuery.isLoading ? (
              <div className="h-16 animate-pulse rounded bg-muted" />
            ) : !insightsQuery.data || insightsQuery.data.priorities.length === 0 ? (
              <p className="text-sm italic text-muted-foreground">
                Nenhuma prioridade identificada.
              </p>
            ) : (
              <ul className="space-y-2">
                {insightsQuery.data.priorities.map((p) => (
                  <li key={p.rank} className="flex items-start gap-2 text-sm">
                    <span
                      className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_BADGE_CLASSES[p.severity]}`}
                    >
                      {SEVERITY_LABELS[p.severity]}
                    </span>
                    <span>
                      <span className="font-medium">{p.title}</span>
                      <span className="block text-xs text-muted-foreground">{p.description}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Desempenho por campanha</CardTitle>
          <p className="text-xs text-muted-foreground">
            Compare o volume e a conversão entre campanhas — {rangeLabel}.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Campanha</th>
                    <th className="px-4 py-2">Primeiros contatos</th>
                    <th className="px-4 py-2">Respostas</th>
                    <th className="px-4 py-2 text-right">Taxa de resposta</th>
                    <th className="px-4 py-2 text-right">Convertidos</th>
                    <th className="px-4 py-2 text-right">Convertidos em outro canal</th>
                    <th className="px-4 py-2 text-right">Receita direta (estimada)</th>
                    <th className="px-4 py-2 text-right">Custo (Meta, estimado)</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data && query.data.campaignBreakdown.length > 0 ? (
                    query.data.campaignBreakdown.map((row) => (
                      <tr key={row.campaign} className="border-b last:border-b-0">
                        <td className="px-4 py-2 text-xs text-muted-foreground">{row.campaign}</td>
                        <td className="px-4 py-2">{row.firstContacts}</td>
                        <td className="px-4 py-2">{row.responses}</td>
                        <td className="px-4 py-2 text-right">{formatPercent(row.responseRate)}</td>
                        <td className="px-4 py-2 text-right text-green-700 dark:text-green-300">
                          {row.converted}
                        </td>
                        <td className="px-4 py-2 text-right text-amber-700 dark:text-amber-300">
                          {row.convertedElsewhere}
                        </td>
                        <td className="px-4 py-2 text-right">{formatUsd(row.revenueUsd)}</td>
                        <td className="px-4 py-2 text-right">{formatUsd(row.costUsd)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhuma campanha com atividade no período selecionado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Comparativo por campanha / template</CardTitle>
          <p className="text-xs text-muted-foreground">
            Primeiros contatos, taxa de resposta e custo estimado — mesmo template comparável entre
            campanhas diferentes.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Campanha</th>
                    <th className="px-4 py-2">Template</th>
                    <th className="px-4 py-2">Primeiros contatos</th>
                    <th className="px-4 py-2">Respostas</th>
                    <th className="px-4 py-2 text-right">Taxa de resposta</th>
                    <th className="px-4 py-2 text-right">Custo (Meta, estimado)</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data && query.data.templateMetrics.length > 0 ? (
                    query.data.templateMetrics.map((row) => (
                      <tr
                        key={`${row.campaign}-${row.template}`}
                        className="border-b last:border-b-0"
                      >
                        <td className="px-4 py-2 text-xs text-muted-foreground">{row.campaign}</td>
                        <td className="px-4 py-2 font-mono text-xs">{row.template}</td>
                        <td className="px-4 py-2">{row.firstContacts}</td>
                        <td className="px-4 py-2">{row.responses}</td>
                        <td className="px-4 py-2 text-right">{formatPercent(row.responseRate)}</td>
                        <td className="px-4 py-2 text-right">
                          {row.costUsd === null ? (
                            <span className="italic text-muted-foreground">sem preço</span>
                          ) : (
                            formatUsd(row.costUsd)
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum primeiro contato (template) no período selecionado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
              <Target className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">Qualidade dos leads</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-2 text-xs text-muted-foreground">
              Intenção mais forte por conversa ({rangeLabel}), classificada pela própria IA.
            </p>
            {query.isLoading ? (
              <div className="h-40 animate-pulse rounded bg-muted" />
            ) : intentDonutData.length === 0 ? (
              <p className="py-8 text-center text-sm italic text-muted-foreground">
                Sem conversas classificadas no período.
              </p>
            ) : (
              <div>
                <div className="relative h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={intentDonutData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={35}
                        outerRadius={60}
                        paddingAngle={2}
                      >
                        {intentDonutData.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xl font-semibold">{totalIntentLeads}</span>
                    <span className="text-[10px] uppercase text-muted-foreground">leads</span>
                  </div>
                </div>
                <ul className="mt-2 space-y-1 text-xs">
                  {intentDonutData.map((d) => (
                    <li key={d.name} className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: d.color }}
                        />
                        {d.name}
                      </span>
                      <span className="font-mono">{d.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
              <CalendarCheck className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">Status dos agendamentos</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-2 text-xs text-muted-foreground">
              Callbacks agendados no período ({rangeLabel}).
            </p>
            {query.isLoading ? (
              <div className="h-40 animate-pulse rounded bg-muted" />
            ) : callbackDonutData.length === 0 ? (
              <p className="py-8 text-center text-sm italic text-muted-foreground">
                Nenhum callback agendado no período.
              </p>
            ) : (
              <div>
                <div className="relative h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={callbackDonutData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={35}
                        outerRadius={60}
                        paddingAngle={2}
                      >
                        {callbackDonutData.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xl font-semibold">{totalCallbacks}</span>
                    <span className="text-[10px] uppercase text-muted-foreground">callbacks</span>
                  </div>
                </div>
                <ul className="mt-2 space-y-1 text-xs">
                  {callbackDonutData.map((d) => (
                    <li key={d.name} className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: d.color }}
                        />
                        {d.name}
                      </span>
                      <span className="font-mono">{d.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">
              <TrendingDown className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">Motivos de perda</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-2 text-xs text-muted-foreground">
              Conversas fechadas/declinadas ({rangeLabel}) — não inclui vendas em outro canal.
            </p>
            {query.isLoading ? (
              <div className="h-40 animate-pulse rounded bg-muted" />
            ) : !query.data || query.data.lossReasons.length === 0 ? (
              <p className="py-8 text-center text-sm italic text-muted-foreground">
                Nenhuma perda registrada no período.
              </p>
            ) : (
              <ul className="space-y-2">
                {query.data.lossReasons.map((r) => (
                  <li key={r.reason}>
                    <div className="flex items-center justify-between text-sm">
                      <span>{lossReasonLabel(r.reason)}</span>
                      <span className="font-mono">
                        {r.count} (
                        {formatPercent(totalLossReasons > 0 ? r.count / totalLossReasons : 0)})
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-red-500"
                        style={{
                          width: `${totalLossReasons > 0 ? (r.count / totalLossReasons) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
              <Clock className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">Últimas interações da IA</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {query.isLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
            ) : !query.data || query.data.recentInteractions.length === 0 ? (
              <p className="p-6 text-center text-sm italic text-muted-foreground">
                Nenhuma conversa encontrada.
              </p>
            ) : (
              <ul className="divide-y">
                {query.data.recentInteractions.map((i) => (
                  <li
                    key={i.conversationId}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium">{i.customerName}</p>
                      <p className="truncate text-xs text-muted-foreground">{i.campaign}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                        {conversationStatusLabel(i.status)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(i.lastActivityAt)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
              <Wallet className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">Impacto financeiro</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-2 text-xs text-muted-foreground">
              Aproximação: só conta receita de conversão direta (produto conhecido).
            </p>
            {query.isLoading ? (
              <div className="h-24 animate-pulse rounded bg-muted" />
            ) : (
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Leads abordados</dt>
                  <dd className="font-mono font-semibold">{query.data?.messagesSent ?? 0}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Custo estimado</dt>
                  <dd className="font-mono font-semibold">
                    {formatUsd(query.data?.investedValueUsd ?? 0)}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Custo por lead</dt>
                  <dd className="font-mono font-semibold">
                    {formatUsd(
                      query.data && query.data.messagesSent > 0
                        ? query.data.investedValueUsd / query.data.messagesSent
                        : 0,
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Receita direta (estimada)</dt>
                  <dd className="font-mono font-semibold text-green-700 dark:text-green-300">
                    {formatUsd(totalRevenueUsd)}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">ROI aproximado</dt>
                  <dd className="font-mono font-semibold">
                    {roiApprox === null ? '—' : `${roiApprox.toFixed(1)}x`}
                  </dd>
                </div>
              </dl>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conversões × declínios ({rangeLabel})</CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          {query.isLoading ? (
            <div className="h-full animate-pulse rounded bg-muted" />
          ) : !query.data || query.data.conversionTrend.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm italic text-muted-foreground">
              Sem dados no período.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={query.data.conversionTrend}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="converted"
                  name="Convertidas"
                  stroke="hsl(142 72% 29%)"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                />
                <Line
                  type="monotone"
                  dataKey="declined"
                  name="Declinadas"
                  stroke="hsl(0 84% 55%)"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {query.data ? (
        <div className="flex items-center justify-end gap-3 text-xs text-muted-foreground">
          <span>
            Período: {formatDateTime(query.data.periodFrom)} – {formatDateTime(query.data.periodTo)}
          </span>
          <span>Atualizado em {formatDateTime(query.data.generatedAt)}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? 'Atualizando...' : 'Atualizar agora'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

const ICON_COLOR_CLASSES = {
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
} as const;

interface KpiCardProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  color?: keyof typeof ICON_COLOR_CLASSES;
}

function KpiCard({ title, children, icon: Icon, color = 'blue' }: KpiCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
        {Icon ? (
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${ICON_COLOR_CLASSES[color]}`}
          >
            <Icon className="h-4 w-4" />
          </div>
        ) : null}
        <CardTitle className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

export default DashboardPage;
